import { computedValue, encounterField, posCoord } from '../../shared/array-types';
import { surfaceField } from '../compile/from-base-encounter-media';
import { readInventoryRows } from '../compile/inventory-raw-readers';
import { floorAt, ceilingAt } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { normalizeTransform, type RestWorld, type RestMover } from '../inventory/floor-move';
import { planInventoryPlacement } from '../movement/placement-plan-22';
import { storedSpawnPoint, type Row } from './row-plan';

export interface InventorySpawnPlacement { world: RestWorld; mover: RestMover; transform: ReturnType<typeof normalizeTransform> }

/** Plain Add's old range/stagger duties; the existing floor-move planner owns support and gravity. */
export function planInventorySpawnPlacement(encounter: Row, values: Row, die: (sides: number) => number,
    placement?: InventorySpawnPlacement): { position: { x: number; y: number; z: number }; mounted?: boolean } {
    const point = { x: Number(values.x_pos ?? 0), y: Number(values.y_pos ?? 0), z: Number(values.z_pos ?? 0) };
    // A scene/drop caller already decided and seated its exact point, including the existing zero-X sentinel.
    if (values.position_ft) return { position: storedSpawnPoint(point) };
    const prefix = !encounterField(encounter, 'encounter_id') && surfaceField(encounter, 'location_id') ? 'location' : 'encounter';
    const field = (name: string): unknown => prefix === 'location' ? surfaceField(encounter, `location_${name}`)
        : encounterField(encounter, `encounter_${name}`);
    const range = (axis: string, side: 'min' | 'max', fallback: number): number => Number(
        computedValue(encounter, prefix, `${prefix}_range_${side}_${axis}`)
        ?? surfaceField(encounter, `${prefix}_range_${side}_${axis}`) ?? fallback);
    const width = Number(field('max_x') ?? 0) || 100;
    const depth = Number(field('max_y') ?? 0) || 100;
    const minX = range('x', 'min', -Math.floor(width / 2)), maxX = range('x', 'max', Math.ceil(width / 2));
    const clamp = (n: number, low: number, high: number): number => Math.max(low, Math.min(high, n));
    if (point.x === 0) {
        const low = Math.max(minX, -10), high = Math.min(maxX, 10);
        const candidates = Array.from({ length: Math.max(0, Math.floor(high) - Math.ceil(low) + 1) }, (_, i) => Math.ceil(low) + i);
        const used = new Set(readInventoryRows(encounter, 'encounter_inventories_arr').map(row => posCoord(row, 'x_pos') ?? 0));
        const free = candidates.filter(x => !used.has(x)), choices = free.length ? free : candidates;
        point.x = choices.length ? choices[die(choices.length) - 1]! : clamp(0, minX, maxX);
    }
    point.x = clamp(point.x, minX, maxX);
    point.y = clamp(point.y, range('y', 'min', -Math.floor(depth / 2)), range('y', 'max', Math.ceil(depth / 2)));
    const minZ = range('z', 'min', Number(field('min_z') ?? -100));
    const maxZ = range('z', 'max', Number(field('max_z') ?? 0) || 1000);
    point.z = clamp(point.z, minZ, maxZ);
    if (!placement) return { position: storedSpawnPoint(point) };
    const { world, mover } = placement;
    // A default workplane may be below an authored floor. Explicit elevations above it still reach the ordinary support planner.
    const ground = world.shape ? floorAt(world.shape, point.x, point.y) : null;
    if (ground !== null) point.z = Math.max(point.z, ground);
    const result = planInventoryPlacement(world, mover, point);
    const ceiling = world.shape ? ceilingAt(world.shape, point.x, point.y) : null;
    const cap = ceiling ?? (Number(field('ceiling') ?? 0) || maxZ);
    result.position.z = clamp(result.position.z, Math.max(minZ, ground ?? minZ), Math.max(ground ?? minZ, Math.min(maxZ, cap)));
    return { position: storedSpawnPoint(result.position), mounted: result.mounted };
}
